import {toFixedLimit} from "../tools/toFixedLimit";

function bitInterleave(x: bigint, y: bigint): bigint {
    x = (x | (x << BigInt(16))) & BigInt("0x0000ffff0000ffff");
    y = (y | (y << BigInt(16))) & BigInt("0x0000ffff0000ffff");
    x = (x | (x << BigInt(8))) & BigInt("0x00ff00ff00ff00ff");
    y = (y | (y << BigInt(8))) & BigInt("0x00ff00ff00ff00ff");
    x = (x | (x << BigInt(4))) & BigInt("0x0f0f0f0f0f0f0f0f");
    y = (y | (y << BigInt(4))) & BigInt("0x0f0f0f0f0f0f0f0f");
    x = (x | (x << BigInt(2))) & BigInt("0x3333333333333333");
    y = (y | (y << BigInt(2))) & BigInt("0x3333333333333333");
    x = (x | (x << BigInt(1))) & BigInt("0x5555555555555555");
    y = (y | (y << BigInt(1))) & BigInt("0x5555555555555555");
    return x | (y << BigInt(1));
}

function bitUnInterleave(w: bigint) {
    w = w & BigInt("0x5555555555555555");
    w = ((w >> BigInt(1)) | w) & BigInt("0x3333333333333333");
    w = ((w >> BigInt(2)) | w) & BigInt("0x0f0f0f0f0f0f0f0f");
    w = ((w >> BigInt(4)) | w) & BigInt("0x00ff00ff00ff00ff");
    w = ((w >> BigInt(8)) | w) & BigInt("0x0000ffff0000ffff");
    w = ((w >> BigInt(16)) | w) & BigInt("0x00000000ffffffff");
    return w;
}

export type GpsPosStr = string;

export class GpsPos {
    readonly Y: number;
    readonly X: number;

    constructor(pos: GpsPosStr);
    constructor(X: number, Y: number);
    constructor(posOrX: GpsPosStr | number, Y?: number) {
        if (typeof posOrX === 'string') {
            const b = BigInt("0x" + posOrX);
            if (b !== BigInt(0)) {
                this.Y = Number(bitUnInterleave(b >> BigInt(1)) & BigInt(0xffffffff));
                this.X = Number(bitUnInterleave(b) & BigInt(0xffffffff));
            } else {
                this.Y = 0;
                this.X = 0;
            }
        } else if (typeof posOrX === 'number' && typeof Y === 'number') {
            this.X = posOrX;
            this.Y = Y;
        } else {
            this.Y = 0;
            this.X = 0;
        }
    }

    isValid(): boolean {
        return this.Y !== 0 || this.X !== 0;
    }

    lat(): number {
        return (900000000 - this.Y) * 0.0000001;
    }

    latStr(): string {
        return toFixedLimit(this.lat(), 7);
    }

    long(): number {
        return (this.X - 1800000000) * 0.0000001;
    }

    longStr(): string {
        return toFixedLimit(this.long(), 7);
    }

    google(): google.maps.LatLng {
        return new google.maps.LatLng(this.lat(), this.long())
    }

    toString(): string {
        return "(" + this.latStr() + ", " + this.longStr() + ")";
    }

    toPosStr(): GpsPosStr {
        let r = bitInterleave(BigInt(this.X), BigInt(this.Y)).toString(16);
        while (r.length < 16) r = "0" + r;
        return r;
    }

    static fromLatLong(lat: number, long: number): GpsPos {
        const y = 900000000 - (Math.round(lat * 10000000) | 0);
        const x = (Math.round(long * 10000000 + 1800000000) | 0);
        return new GpsPos(x, y);
    }

    static fromGoogle(pos: google.maps.LatLng): GpsPos {
        return GpsPos.fromLatLong(pos.lat(), pos.lng());
    }
}

export type GpsBoundStr = string;

export class GpsBound {
    readonly tl: GpsPos;
    readonly br: GpsPos;
    readonly center: GpsPos;
    readonly valid: boolean;

    constructor(str: GpsBoundStr)
    constructor(topLeft: GpsPos, bottomRight: GpsPos)
    constructor(strOrTopLeft: GpsBoundStr | GpsPos, bottomRight?: GpsPos) {
        this.valid = false;
        if (typeof strOrTopLeft == "string") {
            if (strOrTopLeft.length != 33) return;
            this.tl = new GpsPos(strOrTopLeft.substring(0, 16));
            this.br = new GpsPos(strOrTopLeft.substring(17));
        } else if (strOrTopLeft instanceof GpsPos && bottomRight instanceof GpsPos) {
            this.tl = strOrTopLeft;
            this.br = bottomRight;
        } else return;
        this.center = new GpsPos((this.tl.X + this.br.X) >>> 1, (this.tl.Y + this.br.Y) >>> 1);
        this.valid = true;
    }

    google(): google.maps.LatLngBounds {
        return new google.maps.LatLngBounds(
            new google.maps.LatLng(this.br.lat(), this.tl.long()),
            new google.maps.LatLng(this.tl.lat(), this.br.long())
        )
    }

    toString(): string {
        return "((" + toFixedLimit(this.br.lat(), 7) + ", " + toFixedLimit(this.tl.long(), 7) + "), (" + toFixedLimit(this.tl.lat(), 7) + ", " + toFixedLimit(this.br.long(), 7) + "))";
    }

    toBoundStr(): GpsBoundStr {
        return this.tl.toPosStr() + "," + this.br.toPosStr();
    }

    static fromGoogle(bounds: google.maps.LatLngBounds): GpsBound {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        return new GpsBound(GpsPos.fromLatLong(ne.lat(), sw.lng()), GpsPos.fromLatLong(sw.lat(), ne.lng()));
    }
}
